import { watch, reactive, type UnwrapNestedRefs } from 'vue';
import type { BrandId, Errors, VehicleBaseData } from '@dh-io-owpi/backend-api/src/data-contracts';
import { defineStore } from '@dh-io-owpi/shared/src/stores/store';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';
import { assertType } from '@dh-io-owpi/shared/src/utils/assertType';
import { getVehicleBaseData } from '@dh-io-owpi/backend-api/src/Spa';
import type { HttpResponse } from '@dh-io-owpi/backend-api/src/http-client';
import { preloadImage } from '@dh-io-owpi/shared/src/utils/img.util';

import { useAemData } from '@/composables/useAemData';
import { useStageSwitchersStore } from './stageSwitchers';
import { useSectionsStore, sectionTrackLabelMap, type SectionName } from './sections';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { backplateCroppingInfo, generateSrcSet } from '@dh-io-owpi/shared-dynamic-stage/src/utils/bbdImgUrls';
import { currentMediaQuery } from '@dh-io-owpi/shared/src/composables/useMediaQuery';

type VehicleStoreState = Partial<VehicleBaseData> & {
  errorMessage?: string;
  /** brandId is changed after the preload of data and campaign background */
  brandId: BrandId;
};
export const useVehicleStore = defineStore<UnwrapNestedRefs<VehicleStoreState>>('spa-vehicle', () => {
  const aemData = useAemData();
  const switchers = useStageSwitchersStore();
  const { load } = useLoading('stage');
  const sectionsStore = useSectionsStore();
  const { systemError } = useTrackingStore();

  const sections: SectionName[] = ['exterior', 'interior'];

  const state = reactive<VehicleStoreState>({ brandId: switchers.brandId.value });

  let vehicleRequestController: AbortController | null;

  async function fetchData() {
    if (!switchers.selectedPaint.value) return; // just to satisfy typescript
    try {
      const { data } = await getVehicleBaseData(
        aemData.country,
        aemData.language,
        aemData.modelSeries,
        encodeURIComponent(switchers.selectedPaint.value.vehicleId),
        {
          retries: 3,
          signal: vehicleRequestController?.signal,
        },
      );
      if (data.stage.backgroundImageUrl) {
        const srcset = generateSrcSet(data.stage.backgroundImageUrl, backplateCroppingInfo);
        const src = srcset.find((s) => s['data-mq'] === currentMediaQuery.value);
        if (src) await preloadImage(src.srcset);
      }

      sectionsStore.showSections(sections);

      Object.assign(state, data);
    } catch (e) {
      if (assertType<HttpResponse<VehicleBaseData, Errors>>(e, 'error'))
        state.errorMessage = e.error?.errors?.[0]?.message ?? e.error;
      else if (assertType<Error>(e, 'message') && e.name !== 'AbortError') state.errorMessage = e.message;

      sectionsStore.hideSections(sections);

      const sectionsTrackLabel = sections.map((section) => sectionTrackLabelMap[section]);
      systemError({ action: 'pi_pages.sections', label: `load_fail.${sectionsTrackLabel.join(',')}` });
    } finally {
      state.brandId = switchers.brandId.value;
    }
  }

  watch(
    [switchers.selectedStyle, switchers.selectedPaint],
    ([userStyle, userPaint], [oldUserStyle, oldUserPaint]) => {
      // we want to ignore when we set the selectedPaint for the first time, to avoid double fetch
      if (state.stage && ((userStyle && !oldUserStyle) || (userPaint && !oldUserPaint))) return;

      if (vehicleRequestController) vehicleRequestController.abort();

      vehicleRequestController = new AbortController();

      load(fetchData());
    },
    { immediate: true },
  );

  return state;
});
